import type { QuoteLineRow } from '@/v2/stores/useQuotesExportAdminStore'

// Agrégation des lignes de devis « par produit » — partagée entre l'export admin
// (QuotesExportAdminPage) et la fiche client 360° (PartnerQuotesSection).

/** Contribution d'un devis au total d'un produit. */
export interface ProductQuoteContribution {
  quoteReference: string
  quantity: number
  amount: number
}

/** Produit agrégé sur l'ensemble des lignes (toutes lignes de devis confondues). */
export interface ProductGroup {
  key: string
  productName: string
  productCategory: string | null
  totalQuantity: number
  totalAmount: number
  avgUnitPrice: number
  quotes: ProductQuoteContribution[]
}

/**
 * Regroupe les lignes par produit (clé = productOdooId si dispo, sinon productName).
 * Trie les produits par montant HT décroissant et, pour chacun, ses devis par montant décroissant.
 */
export function groupLinesByProduct(lines: QuoteLineRow[]): ProductGroup[] {
  const groups = new Map<string, { g: ProductGroup; quotes: Map<string, ProductQuoteContribution> }>()
  for (const l of lines) {
    const key = l.productOdooId != null ? `id:${l.productOdooId}` : `name:${l.productName}`
    let entry = groups.get(key)
    if (!entry) {
      entry = {
        g: {
          key,
          productName: l.productName,
          productCategory: l.productCategory ?? null,
          totalQuantity: 0,
          totalAmount: 0,
          avgUnitPrice: 0,
          quotes: [],
        },
        quotes: new Map(),
      }
      groups.set(key, entry)
    }
    entry.g.totalQuantity += l.quantity ?? 0
    entry.g.totalAmount += l.priceSubtotal ?? 0
    if (!entry.g.productCategory && l.productCategory) entry.g.productCategory = l.productCategory
    const qc = entry.quotes.get(l.quoteReference) ?? { quoteReference: l.quoteReference, quantity: 0, amount: 0 }
    qc.quantity += l.quantity ?? 0
    qc.amount += l.priceSubtotal ?? 0
    entry.quotes.set(l.quoteReference, qc)
  }
  return [...groups.values()]
    .map(({ g, quotes }) => ({
      ...g,
      avgUnitPrice: g.totalQuantity ? g.totalAmount / g.totalQuantity : 0,
      quotes: [...quotes.values()].sort((a, b) => b.amount - a.amount),
    }))
    .sort((a, b) => b.totalAmount - a.totalAmount)
}
